import { zodResolver } from "@hookform/resolvers/zod";
import { type ContactFormPayload, ContactSubject, contactFormSchema } from "@upsquad/shared";
import { CheckCircle2, ChevronDown, CircleAlert, Send } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CtaButton } from "@/src/common/components/cta-link";
import { submitContactForm } from "@/src/common/services/contact.service";

const SUBJECT_KEYS: Record<ContactSubject, string> = {
	[ContactSubject.FEATURE_REQUEST]: "feature_request",
	[ContactSubject.BUG_REPORT]: "bug_report",
	[ContactSubject.ACCOUNT_REMOVAL]: "account_removal",
	[ContactSubject.GENERAL]: "general",
};

const FIELD_BASE =
	"peer block w-full rounded-2xl border border-default-200 bg-white px-4 pb-2 pt-6 text-sm text-foreground transition placeholder:text-transparent focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15";
const FIELD_ERROR = "border-red-500 focus:border-red-500 focus:ring-red-500/15";
const LABEL_BASE =
	"pointer-events-none absolute left-4 top-2 text-xs font-medium text-default-500 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-default-400 peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:text-brand-700";

type SubmitStatus = { tone: "success" | "error"; message: string } | null;

interface FloatingFieldProps {
	id: string;
	label: string;
	error?: string;
	children: ReactNode;
}

function FloatingField({ id, label, error, children }: FloatingFieldProps) {
	return (
		<div className="space-y-1.5">
			<div className="relative">
				{children}
				<label htmlFor={id} className={LABEL_BASE}>
					{label}
				</label>
			</div>
			{error ? <p className="px-1 text-xs text-red-500">{error}</p> : null}
		</div>
	);
}

export function ContactForm() {
	const { t } = useTranslation(["contact", "common"]);
	const [status, setStatus] = useState<SubmitStatus>(null);

	const {
		register,
		handleSubmit,
		watch,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<ContactFormPayload>({
		resolver: zodResolver(contactFormSchema),
		defaultValues: { name: "", email: "", company: "", subject: ContactSubject.GENERAL, message: "" },
		mode: "onBlur",
	});

	const watchedSubject = watch("subject");
	const isAccountRemoval = watchedSubject === ContactSubject.ACCOUNT_REMOVAL;

	const onSubmit = handleSubmit(async (values) => {
		setStatus(null);
		const result = await submitContactForm(values);
		if (result.ok) {
			setStatus({ tone: "success", message: t("common:toast.contact_success") });
			reset();
			return;
		}
		const message = result.rateLimited ? t("common:toast.contact_rate_limit") : t("common:toast.contact_error");
		setStatus({ tone: "error", message });
	});

	return (
		<form onSubmit={onSubmit} className="space-y-5">
			<div className="grid gap-5 sm:grid-cols-2">
				<FloatingField id="contact-name" label={t("contact:form.name_label")} error={errors.name?.message}>
					<input
						id="contact-name"
						type="text"
						placeholder={t("contact:form.name_placeholder")}
						className={`${FIELD_BASE} ${errors.name ? FIELD_ERROR : ""}`}
						{...register("name")}
					/>
				</FloatingField>

				<FloatingField id="contact-email" label={t("contact:form.email_label")} error={errors.email?.message}>
					<input
						id="contact-email"
						type="email"
						placeholder={t("contact:form.email_placeholder")}
						className={`${FIELD_BASE} ${errors.email ? FIELD_ERROR : ""}`}
						{...register("email")}
					/>
				</FloatingField>
			</div>

			<FloatingField id="contact-company" label={t("contact:form.company_label")}>
				<input
					id="contact-company"
					type="text"
					placeholder={t("contact:form.company_placeholder")}
					className={FIELD_BASE}
					{...register("company")}
				/>
			</FloatingField>

			<div className="space-y-1.5">
				<div className="relative">
					<select
						id="contact-subject"
						className={`block w-full appearance-none rounded-2xl border border-default-200 bg-white px-4 pb-2 pt-6 text-sm text-foreground transition focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 ${errors.subject ? FIELD_ERROR : ""}`}
						{...register("subject")}
					>
						{Object.values(ContactSubject).map((subject) => (
							<option key={subject} value={subject}>
								{t(`contact:form.subjects.${SUBJECT_KEYS[subject]}`)}
							</option>
						))}
					</select>
					<span className="pointer-events-none absolute left-4 top-2 text-xs font-medium text-default-500">
						{t("contact:form.subject_label")}
					</span>
					<ChevronDown
						aria-hidden="true"
						className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-default-400"
					/>
				</div>
				{errors.subject?.message ? <p className="px-1 text-xs text-red-500">{errors.subject.message}</p> : null}
			</div>

			<FloatingField id="contact-message" label={t("contact:form.message_label")} error={errors.message?.message}>
				<textarea
					id="contact-message"
					rows={6}
					placeholder={t("contact:form.message_placeholder")}
					className={`${FIELD_BASE} resize-y ${errors.message ? FIELD_ERROR : ""}`}
					{...register("message")}
				/>
			</FloatingField>

			{isAccountRemoval ? (
				<div className="rounded-2xl border border-brand-500/30 bg-brand-50 px-4 py-3 text-sm text-brand-700">
					{t("contact:form.gdpr_notice")}
				</div>
			) : null}

			<div role="status" aria-live="polite">
				{status ? (
					<p
						className={`flex items-start gap-2 rounded-2xl border px-4 py-3 text-sm ${
							status.tone === "success"
								? "border-success/30 bg-success-soft text-success-soft-foreground"
								: "border-danger/30 bg-danger-soft text-danger-soft-foreground"
						}`}
					>
						{status.tone === "success" ? (
							<CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
						) : (
							<CircleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
						)}
						<span>{status.message}</span>
					</p>
				) : null}
			</div>

			<div className="flex justify-end">
				<CtaButton type="submit" variant="gradient" size="lg" isLoading={isSubmitting} endContent={<Send className="h-4 w-4" />}>
					{isSubmitting ? t("contact:form.submitting") : t("contact:form.submit")}
				</CtaButton>
			</div>
		</form>
	);
}
